import { Button, Card, Center, Loader, Text } from "@mantine/core"
import { useCallback, useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { PageHeader, PageLayout } from "../../../components/crud"
import { useUser } from "../../user/hooks/useUser"
import type { TreeNode } from "../models/documentCategory"
import { useAllCategories } from "../hooks/useDocumentCategories"
import type { DrawerMode } from "./DocumentCategoryDrawer"
import DocumentCategoryDrawer from "./DocumentCategoryDrawer"
import CategoryTreeView from "./CategoryTreeView"

export default function DocumentCategoryList() {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.viewMenuDocumentCategories ?? false

  const { data: items, isLoading } = useAllCategories()

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [drawerOpened, setDrawerOpened] = useState(false)
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("view")
  const [parentId, setParentId] = useState<number | null>(null)

  const params = useParams<{ "*"?: string }>()
  const routeId = params["*"] ? Number(params["*"].split("/")[0]) : NaN

  const openDrawer = useCallback((id: number | null, mode: DrawerMode, pId?: number | null) => {
    setSelectedId(id)
    setDrawerMode(mode)
    setParentId(pId ?? null)
    setDrawerOpened(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setDrawerOpened(false)
    setSelectedId(null)
    setParentId(null)
  }, [])

  useEffect(() => {
    if (!Number.isNaN(routeId) && routeId > 0) {
      openDrawer(routeId, "view")
    }
  }, [routeId, openDrawer])

  const handleDeleted = useCallback(() => { closeDrawer() }, [closeDrawer])
  const handleSwitchToEdit = useCallback(() => { setDrawerMode("edit") }, [])

  const handleSelect = useCallback(
    (node: TreeNode) => {
      if (selectedId === node.id && drawerOpened) {
        closeDrawer()
      } else {
        openDrawer(node.id, "view")
      }
    },
    [openDrawer, closeDrawer, selectedId, drawerOpened],
  )

  const handleEdit = useCallback(
    (node: TreeNode) => {
      openDrawer(node.id, "edit")
    },
    [openDrawer],
  )

  const handleAddChild = useCallback(
    (node: TreeNode) => {
      openDrawer(null, "create", node.id)
    },
    [openDrawer],
  )

  return (
    <PageLayout
      header={
        <PageHeader
          title={t("documentCategories.title")}
          actions={
            canManage ? (
              <Button onClick={() => openDrawer(null, "create")} leftSection={<i className="fa fa-plus" />}>
                <Trans i18nKey="documentCategories.create" />
              </Button>
            ) : undefined
          }
        />
      }
    >
      <Card withBorder p={0} radius="md">
        {isLoading ? (
          <Center h={200}><Loader /></Center>
        ) : !items || items.length === 0 ? (
          <Text p="md" c="dimmed" ta="center">
            {t("documentCategories.empty")}
          </Text>
        ) : (
          <CategoryTreeView
            items={items}
            selectedId={selectedId}
            onSelect={handleSelect}
            onEdit={handleEdit}
            onAddChild={handleAddChild}
          />
        )}
      </Card>
      <DocumentCategoryDrawer
        opened={drawerOpened}
        onClose={closeDrawer}
        mode={drawerMode}
        selectedId={selectedId}
        onDeleted={handleDeleted}
        onSwitchToEdit={handleSwitchToEdit}
        parentId={parentId}
      />
    </PageLayout>
  )
}
